import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, LogIn, LogOut, AlertCircle } from 'lucide-react';
import PunchDialog from '../components/PunchDialog';

const MyAttendance = () => {
  const [records, setRecords] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punchOpen, setPunchOpen] = useState(false);
  const [punchType, setPunchType] = useState('in');
  const [today, setToday] = useState(null);
  const [corrDialog, setCorrDialog] = useState(false);
  const [corrForm, setCorrForm] = useState({ date: '', requested_punch_in: '', requested_punch_out: '', reason: '' });
  const [submitCorr, setSubmitCorr] = useState(false);

  const fetchData = async () => {
    try {
      const [att, corr] = await Promise.all([
        api.get('/me/attendance'),
        api.get('/me/attendance/corrections'),
      ]);
      setRecords(att.data);
      setCorrections(corr.data);
      const todayStr = new Date().toISOString().split('T')[0];
      setToday(att.data.find(r => r.date === todayStr) || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openPunchDialog = (type) => {
    setPunchType(type);
    setPunchOpen(true);
  };

  const submitCorrection = async () => {
    if (!corrForm.date || !corrForm.reason || corrForm.reason.trim().length < 5) {
      toast.error('Date and a reason (min 5 chars) are required');
      return;
    }
    const payload = {
      date: corrForm.date,
      reason: corrForm.reason,
      requested_punch_in: corrForm.requested_punch_in ? `${corrForm.date}T${corrForm.requested_punch_in}:00+00:00` : null,
      requested_punch_out: corrForm.requested_punch_out ? `${corrForm.date}T${corrForm.requested_punch_out}:00+00:00` : null,
    };
    setSubmitCorr(true);
    try {
      await api.post('/me/attendance/correction', payload);
      toast.success('Correction request submitted');
      setCorrDialog(false);
      setCorrForm({ date: '', requested_punch_in: '', requested_punch_out: '', reason: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubmitCorr(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Attendance</h1>
          <p className="text-muted-foreground">Punch in/out and view your attendance history</p>
        </div>
        <Dialog open={corrDialog} onOpenChange={setCorrDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="request-correction-btn">
              <AlertCircle size={16} className="mr-2" />Request Correction
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request Attendance Correction</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Date</Label><Input type="date" value={corrForm.date} onChange={(e) => setCorrForm({...corrForm, date: e.target.value})} data-testid="corr-date" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Punch In (HH:MM)</Label><Input type="time" value={corrForm.requested_punch_in} onChange={(e) => setCorrForm({...corrForm, requested_punch_in: e.target.value})} data-testid="corr-in" /></div>
                <div><Label className="text-xs">Punch Out (HH:MM)</Label><Input type="time" value={corrForm.requested_punch_out} onChange={(e) => setCorrForm({...corrForm, requested_punch_out: e.target.value})} data-testid="corr-out" /></div>
              </div>
              <div><Label>Reason</Label><Textarea value={corrForm.reason} onChange={(e) => setCorrForm({...corrForm, reason: e.target.value})} placeholder="e.g. Forgot to punch out — was at client site" data-testid="corr-reason" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCorrDialog(false)}>Cancel</Button>
              <Button onClick={submitCorrection} disabled={submitCorr} data-testid="submit-correction-btn">
                {submitCorr && <Loader2 size={14} className="animate-spin mr-1" />}Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Today</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Punch In</p>
              <p className="text-2xl font-bold">{today?.punch_in ? new Date(today.punch_in).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Punch Out</p>
              <p className="text-2xl font-bold">{today?.punch_out ? new Date(today.punch_out).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            {!today?.punch_in && (
              <Button onClick={() => openPunchDialog('in')} data-testid="punch-in-btn">
                <LogIn size={16} className="mr-2" />Punch In
              </Button>
            )}
            {today?.punch_in && !today?.punch_out && (
              <Button onClick={() => openPunchDialog('out')} variant="destructive" data-testid="punch-out-btn">
                <LogOut size={16} className="mr-2" />Punch Out
              </Button>
            )}
            {today?.punch_in && today?.punch_out && (
              <Badge>Total: {today.total_hours} hrs</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <PunchDialog
        open={punchOpen}
        onOpenChange={setPunchOpen}
        punchType={punchType}
        onSuccess={fetchData}
      />

      <Tabs defaultValue="history">
        <TabsList>
          <TabsTrigger value="history" data-testid="tab-att-history">History</TabsTrigger>
          <TabsTrigger value="corrections" data-testid="tab-att-corrections">My Corrections ({corrections.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="history">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Punch In</TableHead><TableHead>Punch Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {records.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No records yet</TableCell></TableRow>}
                  {records.slice(0, 30).map(r => (
                    <TableRow key={r.id} data-testid={`att-row-${r.id}`}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell>{r.punch_in ? new Date(r.punch_in).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</TableCell>
                      <TableCell>{r.punch_out ? new Date(r.punch_out).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '—'}</TableCell>
                      <TableCell>{r.total_hours || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.status}</Badge>
                        {r.corrected && <Badge variant="secondary" className="ml-1 text-xs">corrected</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="corrections">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead><TableHead>Remarks</TableHead></TableRow></TableHeader>
                <TableBody>
                  {corrections.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No correction requests yet</TableCell></TableRow>}
                  {corrections.map(c => (
                    <TableRow key={c.id} data-testid={`my-corr-${c.id}`}>
                      <TableCell>{c.date}</TableCell>
                      <TableCell className="text-xs">{c.requested_punch_in ? new Date(c.requested_punch_in).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '—'}</TableCell>
                      <TableCell className="text-xs">{c.requested_punch_out ? new Date(c.requested_punch_out).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '—'}</TableCell>
                      <TableCell><Badge variant={c.status === 'approved' ? 'default' : c.status === 'rejected' ? 'destructive' : 'secondary'}>{c.status}</Badge></TableCell>
                      <TableCell className="max-w-xs truncate">{c.reason}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.remarks || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyAttendance;
